import { useEffect, useState } from 'react';
import { onUiEvent } from '../../events/error/errorEventBus';

type Toast = {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
  state: 'init' | 'enter' | 'leave';
};

const TOAST_DURATION = 3600;
const TOAST_LEAVE_OFFSET = 420;
const INTERACTION_DISMISS_DELAY = 320;

/**
 * 监听全局 Toast 事件并在页面右上角渲染临时提示。
 * @returns Toast 列表渲染结果，为空时返回 null
 */
export function ToastListener() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const dispose = onUiEvent('ui:toast', (detail) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, ...detail, message: detail.message ?? '', state: 'init' }]);
      window.requestAnimationFrame(() => {
        setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, state: 'enter' } : t)));
      });
      window.setTimeout(() => {
        setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, state: 'leave' } : t)));
      }, Math.max(0, TOAST_DURATION - TOAST_LEAVE_OFFSET));
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, TOAST_DURATION);
    });
    return dispose;
  }, []);

  useEffect(() => {
    if (!toasts.length) return;
    const dismissAll = () => {
      setToasts((prev) => prev.map((t) => ({ ...t, state: 'leave' })));
      window.setTimeout(() => setToasts([]), INTERACTION_DISMISS_DELAY);
    };
    const opts = { passive: true } as const;
    window.addEventListener('pointerdown', dismissAll, opts);
    window.addEventListener('keydown', dismissAll);
    window.addEventListener('scroll', dismissAll, opts);
    return () => {
      window.removeEventListener('pointerdown', dismissAll);
      window.removeEventListener('keydown', dismissAll);
      window.removeEventListener('scroll', dismissAll);
    };
  }, [toasts.length]);

  if (!toasts.length) return null;

  return (
    <div className="fixed left-1/2 top-1/2 z-[20000] flex max-w-[360px] -translate-x-1/2 -translate-y-1/2 flex-col gap-[10px]">
      {toasts.map((toast) => {
        const accent = toast.type === 'success'
          ? '#34d399'
          : toast.type === 'error'
            ? '#f97316'
            : '#60a5fa';
        const title = toast.type === 'success'
          ? 'Added'
          : toast.type === 'error'
            ? 'Inventory'
            : 'Notice';
        return (
          <div
            key={toast.id}
            className="cart-toast w-[240px] min-w-[240px] max-w-[240px] rounded-[20px] border border-black/5 bg-white/95 px-4 py-6 text-[#1d1d1f]"
            data-state={toast.state}
            style={{ ['--toast-accent' as string]: accent }}
          >
            <div className="cart-toast__accent" />
            <div className="flex flex-col items-center gap-4 text-center">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-[14px] border border-black/10 bg-black/5" aria-hidden="true">
                {toast.type === 'success' && (
                  <svg className="h-4 w-4" style={{ color: accent }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="h-4 w-4" style={{ color: accent }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                    <path d="M10.2 4h3.6l6.2 11.2-1.8 3.3H5.8L4 15.2z" />
                  </svg>
                )}
                {toast.type === 'info' && (
                  <svg className="h-4 w-4" style={{ color: accent }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 8h.01" />
                    <path d="M11 12h1v4h1" />
                    <circle cx="12" cy="12" r="9" />
                  </svg>
                )}
              </span>
              <div>
                <div className="hidden">{title}</div>
                <div className="text-[14px] font-semibold leading-[1.3] text-[#1d1d1f]">{toast.message}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
